import { DialogTitle } from "@mui/material";
import { DynamicForm } from "commons/ultilities/dynamic-form/components/dynamic-form";
import { DynamicFormArray } from "commons/ultilities/dynamic-form/components/dynamic-form-array";
import { useForm } from "commons/ultilities/dynamic-form/hooks/use-form";
import { IControl } from "commons/ultilities/dynamic-form/models/control";
import dayjs from "dayjs";
import { observer } from "mobx-react-lite";
import { useStores } from "mobx-stores/_root-store";
import { useResetCustomerDependentControls } from "pages/common/hooks/use-reset-customer-dependent-controls";
import { useCallback, useEffect, useMemo, useState } from "react";
import { DialogComplexContent } from "share-components/templates/form/dialog-complex-form/dialog-complex-content";
import { DialogComplexFormContainer } from "share-components/templates/form/dialog-complex-form/dialog-complex-form";
import { DialogComplexFormArrayContainer } from "share-components/templates/form/dialog-complex-form/dialog-complex-form-array";

import { deliveryNoteBusiness } from "api/bussiness/delivery-note/delivery-note-business";
import { dateTimeToUTCString } from "commons/ultilities/date/dayjs";
import { createProductOptionLabel } from "commons/ultilities/option-item/label-ultil";
import { IUpdateDeliveryNote } from "mobx-stores/_models/delivery-note/delivery-note";
import { DialogForm } from "share-components/templates/form/dialog-form";
import { getEditDeliveryNoteFormSchema } from "../schema";

export const EditDeliveryNote = observer(() => {
  const {
    deliveryNoteManagement: {
      openEdit,
      getDeliveryNoteDetail,
      setProp,
      deliveryNoteDetail,
    },
  } = useStores();
  const [enableDependentReset, setEnableDependentReset] = useState(false);
  const {
    form: { controls, isValid },
    getControl,
    getControlArray,
    setDefaults,
    submitHandler,
  } = useForm<IUpdateDeliveryNote>(
    (form) => getEditDeliveryNoteFormSchema(form),
    {
      id: "",
      date: dateTimeToUTCString(dayjs()),
      number: "",
      man: "",
      licensePalate: "",
      note: "",
      deliveryAddressId: "",
      customerId: "",
      purchaseOrderId: "",
      items: [],
    },
  );

  useEffect(() => {
    if (deliveryNoteDetail) {
      setEnableDependentReset(false);

      setDefaults({
        id: deliveryNoteDetail.id,
        date: deliveryNoteDetail.date,
        number: deliveryNoteDetail.number,
        man: deliveryNoteDetail.man,
        purchaseOrderId: deliveryNoteDetail.purchaseOrderId,
        licensePalate: deliveryNoteDetail.licensePalate,
        note: deliveryNoteDetail.note,
        deliveryAddressId: deliveryNoteDetail.deliveryAddressId,
        customerId: deliveryNoteDetail.customerId,
        items: deliveryNoteDetail.items.map((x) => ({
          id: x.id,
          amount: x.amount,
          note: x.note,
          productId: x.productId,
          mh: x.mh,
          weight: x.weight,
          package: x.package,
        })),
      });
      if (deliveryNoteDetail.items && deliveryNoteDetail.items.length > 0) {
        // const itemsCtrs = getControlArray("items").forEach((x, idx) => {
        //   const data = deliveryNoteDetail.items.filter(
        //     (x) => idCtr && x.id === idCtr.value
        //   )?.[0];
        //   const row = getArrayItemControl("items", idx);
        //   const productIdCtr = row.getControl("productId");
        //   productIdCtr.setProp("optionItems", [
        //     {
        //       label: createProductOptionLabel(data.product),
        //       value: data.product?.id,
        //     },
        //   ]);
        // });
        getControl("items").children.forEach((row: IControl[]) => {
          const idCtr = row.filter((x) => x.name === "id")?.[0];
          const data = deliveryNoteDetail.items.filter(
            (x) => idCtr && x.id === idCtr.value,
          )?.[0];
          const productIdCtr = row.filter((x) => x.name === "productId")?.[0];
          if (productIdCtr && data.product) {
            productIdCtr.setProp("optionItems", [
              {
                label: createProductOptionLabel(data.product),
                value: data.product?.id,
              },
            ]);
          }
        });
      }

      setEnableDependentReset(true);
    }
  }, [getControl, deliveryNoteDetail, setDefaults]);

  useEffect(() => {
    const customerCtr = getControl("purchaseOrderId");
    customerCtr.setProp("optionItems", [
      {
        value: deliveryNoteDetail?.purchaseOrderId,
        label: `${deliveryNoteDetail?.purchaseOrder?.poNumber || ""}`,
      },
    ]);
  }, [
    deliveryNoteDetail?.purchaseOrder?.poNumber,
    deliveryNoteDetail?.purchaseOrderId,
    getControl,
  ]);

  useEffect(() => {
    const customerSelection = getControl("customerId");
    customerSelection.setProp("optionItems", [
      {
        label: deliveryNoteDetail?.customer?.dealingName || "",
        value: deliveryNoteDetail?.customer?.id,
      },
    ]);
  }, [
    getControl,
    deliveryNoteDetail?.customer?.id,
    deliveryNoteDetail?.customer?.dealingName,
  ]);

  useEffect(() => {
    const deliveryAddressCtr = getControl("deliveryAddressId");
    deliveryAddressCtr.setProp("optionItems", [
      {
        value: deliveryNoteDetail?.deliveryAddress?.id,
        label: `${deliveryNoteDetail?.deliveryAddress?.address || ""}`,
      },
    ]);
  }, [
    getControl,
    deliveryNoteDetail?.deliveryAddress?.address,
    deliveryNoteDetail?.deliveryAddress?.id,
  ]);

  const saveHandler = useCallback(
    async (data: IUpdateDeliveryNote) => {
      try {
        if (!deliveryNoteDetail?.id) return;
        await deliveryNoteBusiness.update(deliveryNoteDetail?.id, data);
        await getDeliveryNoteDetail(deliveryNoteDetail?.id);
        setProp("openEdit", false);
      } catch (ex) {
        console.error(ex);
      }
    },
    [getDeliveryNoteDetail, deliveryNoteDetail?.id, setProp],
  );

  const itemControl = useMemo(() => {
    return getControl("items");
  }, [getControl]);

  const customerControl = useMemo(() => {
    return getControl("customerId");
  }, [getControl]);

  const deliveryAddressControl = useMemo(() => {
    return getControl("deliveryAddressId");
  }, [getControl]);

  useResetCustomerDependentControls({
    customerId: customerControl.value,
    controls: [{ control: deliveryAddressControl }],
    arrayChildControls: [
      {
        arrayControl: itemControl,
        childControlName: "productId",
      },
    ],
    enabled: enableDependentReset,
  });

  const deliveryNoteItems = useMemo(() => {
    return getControlArray("items");
  }, [getControlArray]);

  const removeRowHandler = useCallback(
    (idx: number) => {
      itemControl.removeChildByIndex(idx);
    },
    [itemControl],
  );

  const addNewDeliveryNoteItemRow = useCallback(() => {
    itemControl.addNewChild();
  }, [itemControl]);

  return (
    <DialogForm
      open={openEdit}
      fullWidth
      maxWidth={"xl"}
      onClose={() => setProp("openEdit", false)}
      onCancelBtnClicked={() => setProp("openEdit", false)}
      onSaveBtnClick={async () => await submitHandler(saveHandler)}
      saveBtnProps={{
        disabled: !isValid,
      }}
    >
      <DialogTitle>Sửa phiếu giao hàng</DialogTitle>
      <DialogComplexContent>
        <DialogComplexFormContainer>
          <DynamicForm controls={controls}></DynamicForm>
        </DialogComplexFormContainer>
        <DialogComplexFormArrayContainer
          label={itemControl.label}
          addNewRow={addNewDeliveryNoteItemRow}
        >
          {deliveryNoteItems && deliveryNoteItems.length > 0 && (
            <DynamicFormArray
              containerProps={{
                sx: {
                  marginTop: "10px",
                  marginBottom: "10px",
                },
              }}
              children={deliveryNoteItems}
              onRemoveRowClick={removeRowHandler}
            ></DynamicFormArray>
          )}
        </DialogComplexFormArrayContainer>
      </DialogComplexContent>
    </DialogForm>
  );
});
